/**
 * Componente ScrollProgress
 * Indicador de progreso de scroll en la parte superior de la página
 * Estilo minimalista tipo Apple
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Barra de progreso de scroll
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.color - Color de la barra de progreso
 * @param {number} props.height - Altura de la barra en píxeles
 * @param {string} props.position - Posición ('top' o 'bottom')
 * @param {number} props.zIndex - z-index de la barra
 * @returns {JSX.Element} Barra de progreso
 */
const ScrollProgress = ({
  color = '#C1052F',
  height = 3,
  position = 'top',
  zIndex = 9999,
}) => {
  const progressRef = useRef(null);

  useEffect(() => {
    const progressBar = progressRef.current;
    if (!progressBar) return;

    // Animar la barra de progreso según el scroll
    gsap.to(progressBar, {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: document.body,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.3,
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === document.body) {
          trigger.kill();
        }
      });
    };
  }, []);

  const positionStyles = position === 'top' 
    ? { top: 0 } 
    : { bottom: 0 };

  return (
    <div
      ref={progressRef}
      className="fixed left-0 w-full pointer-events-none"
      style={{
        ...positionStyles,
        height: `${height}px`,
        backgroundColor: color,
        transformOrigin: 'left center',
        transform: 'scaleX(0)',
        zIndex,
      }}
    />
  );
};

ScrollProgress.propTypes = {
  color: PropTypes.string,
  height: PropTypes.number,
  position: PropTypes.oneOf(['top', 'bottom']),
  zIndex: PropTypes.number,
};

export default ScrollProgress;
